import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCreateProject, useUpdateProject } from '@/hooks/use-projects';
import { useCreateClient } from '@/hooks/use-clients';
import { useCreateUser, useUsers } from '@/hooks/use-users';
import { useClients } from '@/hooks/use-clients';
import { toast } from '@/hooks/use-toast';
import { Loader2, Building2, UserPlus, FileText, ChevronDown, Layers } from 'lucide-react';
import { useTemplates } from '@/hooks/use-templates';
import { FormField, useFieldValidation, validators } from '@/components/ui/form-field';
import { cn } from '@/lib/utils';
import type { UserRole } from 'shared';

interface ProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project?: any;
}

const stages = [
  { value: 'enquiry', label: 'Enquiry' },
  { value: 'quote', label: 'Quote' },
  { value: 'design', label: 'Design' },
  { value: 'develop', label: 'Develop' },
  { value: 'review', label: 'Review' },
  { value: 'launch', label: 'Launch' },
  { value: 'finalised', label: 'Finalised' },
];

// ─── Child dialog: Create Client (nested on top of project dialog) ─────────
function CreateClientDialog({
  open,
  onOpenChange,
  onClientCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClientCreated: (clientId: string) => void;
}) {
  const createClient = useCreateClient();
  const createUser = useCreateUser();
  const { refetch: refetchClients } = useClients();

  const [companyName, setCompanyName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [clientNotes, setClientNotes] = useState('');
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userRole, setUserRole] = useState<UserRole>('client');
  const [userPhone, setUserPhone] = useState('');

  const isPending = createUser.isPending || createClient.isPending;

  const resetForm = () => {
    setCompanyName('');
    setClientPhone('');
    setBillingAddress('');
    setClientNotes('');
    setUserName('');
    setUserEmail('');
    setUserRole('client');
    setUserPhone('');
  };

  const handleSubmit = async () => {
    if (!companyName.trim() || !userName.trim() || !userEmail.trim()) return;

    try {
      const userRes = await createUser.mutateAsync({
        email: userEmail.trim(),
        name: userName.trim(),
        role: userRole,
        phone: userPhone.trim() || undefined,
      });
      const createdUser = userRes.data;
      if (!createdUser?.id) throw new Error('Failed to create user');

      const clientRes = await createClient.mutateAsync({
        userId: createdUser.id,
        companyName: companyName.trim(),
        phone: clientPhone.trim() || undefined,
        billingAddress: billingAddress.trim() || undefined,
        notes: clientNotes.trim() || undefined,
      });
      const createdClient = clientRes.data;

      if (createdClient?.id) {
        await refetchClients();
      }

      onClientCreated(createdClient?.id);
      resetForm();
      onOpenChange(false);
      toast({
        title: 'Client created',
        description: `${companyName.trim()} has been created and an invitation sent.`,
        variant: 'success',
      });
    } catch (err: any) {
      toast({
        title: 'Failed to create client',
        description: err?.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onOpenChange(false); resetForm(); } }}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 size={18} className="text-dyndev-green" />
            Create New Client
          </DialogTitle>
          <DialogDescription>
            Create a user and client account in one step.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <FormField label="Company Name *">
            <Input
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Acme Corp"
              required
            />
          </FormField>

          <div className="rounded-lg border border-[var(--border-color)] p-4 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <UserPlus size={14} className="text-[var(--text-secondary)]" />
              <span className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                User Details
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label="Full Name *">
                <Input
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="John Doe"
                  required
                />
              </FormField>
              <FormField label="Email *">
                <Input
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="john@example.com"
                  required
                />
              </FormField>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label="Role *">
                <Select value={userRole} onValueChange={(value) => setUserRole(value as UserRole)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="project_manager">Project Manager</SelectItem>
                    <SelectItem value="support_staff">Support Staff</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Phone">
                <Input
                  type="tel"
                  value={userPhone}
                  onChange={(e) => setUserPhone(e.target.value)}
                  placeholder="+44 20 1234 5678"
                />
              </FormField>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField label="Client Phone">
              <Input
                type="tel"
                value={clientPhone}
                onChange={(e) => setClientPhone(e.target.value)}
                placeholder="+44 20 1234 5678"
              />
            </FormField>
            <FormField label="Billing Address">
              <Input
                value={billingAddress}
                onChange={(e) => setBillingAddress(e.target.value)}
                placeholder="123 Main Street, London..."
              />
            </FormField>
          </div>

          <FormField label="Notes">
            <Textarea
              value={clientNotes}
              onChange={(e) => setClientNotes(e.target.value)}
              placeholder="Any additional notes..."
              rows={2}
            />
          </FormField>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => { onOpenChange(false); resetForm(); }}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isPending || !companyName.trim() || !userName.trim() || !userEmail.trim()}
            >
              {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Client
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Project Dialog ──────────────────────────────────────────────────
export function ProjectDialog({ open, onOpenChange, project }: ProjectDialogProps) {
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const { data: clientsData, isLoading: clientsLoading } = useClients();
  const { data: usersData } = useUsers();
  const allUsers = usersData?.data || [];
  // Show only admin + project_manager as potential PMs
  const eligiblePMs = allUsers.filter((u: any) => u.isActive !== false && (u.role === 'admin' || u.role === 'project_manager'));
  const isEditing = !!project;
  const clients = clientsData?.data || [];

  const [clientId, setClientId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [stage, setStage] = useState('enquiry');
  const [startDate, setStartDate] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [budget, setBudget] = useState('');
  const [pmId, setPmId] = useState('');

  // Inline validation
  const nameValidation = useFieldValidation([validators.required('Project name is required')]);

  // Nested dialog for creating a client
  const [createClientOpen, setCreateClientOpen] = useState(false);

  // Templates
  const { data: templatesData } = useTemplates();
  const templates = templatesData?.data || [];
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);

  useEffect(() => {
    if (project) {
      setClientId(project.clientId || '');
      setName(project.name || '');
      nameValidation.setValue(project.name || '');
      setDescription(project.description || '');
      setStage(project.stage || 'enquiry');
      setStartDate(project.startDate ? project.startDate.split('T')[0] : '');
      setTargetDate(project.targetDate ? project.targetDate.split('T')[0] : '');
      setBudget(project.budget?.toString() || '');
      setPmId(project.pmId || '');
      setSelectedTemplate(null);
      setShowTemplatePicker(false);
    } else {
      setClientId('');
      setName('');
      nameValidation.setValue('');
      setDescription('');
      setStage('enquiry');
      setStartDate('');
      setTargetDate('');
      setBudget('');
      setPmId('');
      setCreateClientOpen(false);
      setSelectedTemplate(null);
      setShowTemplatePicker(false);
    }
  }, [project, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate name before submission
    nameValidation.onBlur();
    if (nameValidation.error) {
      return;
    }

    const data: any = {
      name: name.trim(),
      description: description.trim() || undefined,
      clientId,
      stage,
      templateId: selectedTemplate?.id || undefined,
      startDate: startDate || undefined,
      targetDate: targetDate || undefined,
      budget: budget ? parseFloat(budget) : undefined,
      pmId: pmId || undefined,
    };

    if (!isEditing && !data.clientId) return;

    try {
      if (isEditing) {
        await updateProject.mutateAsync({ id: project.id, ...data });
        toast({ title: 'Project updated', description: `"${data.name}" has been updated.`, variant: 'success' });
      } else {
        await createProject.mutateAsync(data);
        toast({ title: 'Project created', description: `"${data.name}" has been created.`, variant: 'success' });
      }
      onOpenChange(false);
    } catch (err: any) {
      toast({
        title: `Failed to ${isEditing ? 'update' : 'create'} project`,
        description: err?.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  const isPending = createProject.isPending || updateProject.isPending;
  const selectedClient = clients.find((c: any) => c.id === clientId);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Project' : 'Create Project'}</DialogTitle>
            <DialogDescription>
              {isEditing ? 'Update the project details below.' : 'Add a new project to your workspace.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Client selector — only show when creating */}
            {!isEditing && (
              <div className="space-y-2">
                <Label>Client</Label>
                <Select value={clientId} onValueChange={setClientId}>
                  <SelectTrigger>
                    <SelectValue placeholder={clientsLoading ? 'Loading clients...' : 'Select a client'} />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.length === 0 && !clientsLoading && (
                      <div className="px-2 py-4 text-center text-xs text-[var(--text-secondary)]">
                        No clients found. Create one first.
                      </div>
                    )}
                    {clients.map((c: any) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.companyName} — {c.user?.name || c.user?.email}
                      </SelectItem>
                    ))}
                    <div className="border-t border-[var(--border-color)] my-1" />
                    <button
                      type="button"
                      onClick={() => { setCreateClientOpen(true); setClientId(''); }}
                      className="relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-xs font-medium text-dyndev-green hover:bg-dyndev-green/10 outline-none transition-colors gap-2"
                    >
                      <Building2 size={14} />
                      Create New Client
                    </button>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Selected client badge */}
            {!isEditing && clientId && selectedClient && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-dyndev-green/5 border border-dyndev-green/10">
                <Building2 size={14} className="text-dyndev-green flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{selectedClient.companyName}</p>
                  <p className="text-[10px] text-[var(--text-secondary)] truncate">
                    {selectedClient.user?.name || selectedClient.user?.email}
                  </p>
                </div>
              </div>
            )}

            {/* Project name with inline validation */}
            <FormField
              label="Project Name"
              error={nameValidation.error}
              touched={nameValidation.touched}
              dirty={nameValidation.dirty}
              showError={nameValidation.showError}
              isValid={nameValidation.isValid}
            >
              <Input
                id="name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  nameValidation.onChange(e);
                }}
                onBlur={nameValidation.onBlur}
                placeholder="Enter project name"
                required
                className={cn(
                  nameValidation.showError && 'border-red-400/50 focus:border-red-400 focus:ring-red-400',
                  nameValidation.isValid && nameValidation.dirty && 'border-dyndev-green/50',
                )}
              />
            </FormField>

            <FormField label="Description">
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional project description"
                rows={3}
                className="flex w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] focus:outline-none focus:border-dyndev-green focus:ring-1 focus:ring-dyndev-green transition-all duration-200 resize-none"
              />
            </FormField>

            {/* Template selector when creating */}
            {!isEditing && templates.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Project Template</Label>
                  <button
                    type="button"
                    onClick={() => setShowTemplatePicker(!showTemplatePicker)}
                    className="text-xs text-dyndev-green hover:text-dyndev-green/80 transition-colors flex items-center gap-1"
                  >
                    <Layers size={12} />
                    {selectedTemplate ? 'Change template' : 'Browse templates'}
                  </button>
                </div>

                {selectedTemplate ? (
                  <div className="flex items-center gap-3 p-3 rounded-lg border border-dyndev-green/20 bg-dyndev-green/5">
                    <div className="w-8 h-8 rounded-lg bg-dyndev-green/10 flex items-center justify-center flex-shrink-0">
                      <FileText size={14} className="text-dyndev-green" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{selectedTemplate.name}</p>
                      <p className="text-xs text-[var(--text-secondary)]">
                        {selectedTemplate.category || 'General'} · {selectedTemplate.stages?.length || 0} stages ·{' '}
                        {selectedTemplate.tasks?.length || 0} default tasks
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedTemplate(null);
                        setShowTemplatePicker(false);
                      }}
                      className="text-xs text-[var(--text-secondary)] hover:text-red-400 transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                ) : showTemplatePicker ? (
                  <div className="space-y-1.5 max-h-[200px] overflow-y-auto rounded-lg border border-[var(--border-color)] p-1.5">
                    {templates.map((tpl: any) => (
                      <button
                        key={tpl.id}
                        type="button"
                        onClick={() => {
                          setSelectedTemplate(tpl);
                          setShowTemplatePicker(false);
                          if (tpl.stages?.[0]) {
                            setStage(tpl.stages[0]);
                          }
                        }}
                        className="w-full flex items-start gap-3 p-2.5 rounded-lg hover:bg-[var(--bg-secondary)] transition-colors text-left"
                      >
                        <div className="w-7 h-7 rounded-md bg-dyndev-green/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <FileText size={12} className="text-dyndev-green" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{tpl.name}</p>
                          {tpl.description && (
                            <p className="text-xs text-[var(--text-secondary)] line-clamp-1">{tpl.description}</p>
                          )}
                        </div>
                        {tpl.category && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-secondary)] text-[var(--text-secondary)]">
                            {tpl.category}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            )}

            <div className="space-y-2">
              <Label>Stage</Label>
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {stages.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <FormField label="Start Date">
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </FormField>
              <FormField label="Target Date">
                <Input
                  type="date"
                  value={targetDate}
                  onChange={(e) => setTargetDate(e.target.value)}
                />
              </FormField>
            </div>

            <FormField label="Budget (£)">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                placeholder="e.g. 5000"
              />
            </FormField>

            {/* PM Selector */}
            <FormField label="Project Manager">
              <Select value={pmId} onValueChange={setPmId}>
                <SelectTrigger>
                  <SelectValue placeholder="Auto-assigned (you)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Auto-assigned (creator)</SelectItem>
                  {eligiblePMs.map((u: any) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} ({u.role === 'admin' ? 'Admin' : 'PM'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[var(--text-secondary)] mt-1">
                Leave as "Auto-assigned" to default to the project creator. Admins and project managers are eligible.
              </p>
            </FormField>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending || !name.trim() || (!isEditing && !clientId)}>
                {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isEditing ? 'Update Project' : 'Create Project'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Nested client creation dialog */}
      <CreateClientDialog
        open={createClientOpen}
        onOpenChange={setCreateClientOpen}
        onClientCreated={(id) => setClientId(id)}
      />
    </>
  );
}
